import React, { useRef, useState, useEffect, useCallback, useMemo } from 'react';
import { CrackAnalysis, Severity, InspectionMode, Finding, TelemetryPoint, ImageResolutionMode, IMAGE_RESOLUTION_OPTIONS } from '../types';
import { analyzeSteelSequence, analyzeStaticSurface, analyzeTwoPass } from '../services/claudeService';
import { parseTrajectoryCsv, detectMissionStart, calculateAssetGeometry, parseLasFile, parseTextPointCloud, filterPointsToAssetBoundary, detectTrajectoryOrientation } from '../services/telemetryService';
import { TrajectoryMapT } from './TrajectoryMapT';
import { exportFindingsToZip } from '../services/zipService';
import { saveFileToProject, saveSessionCheckpoint } from '../services/fileSystemService';
import { saveEntryToVault } from '../services/vaultService';
import { computeWallAnchor, deduplicateFindings, MergeGroup } from '../services/deduplicationService';
import { LoadingOverlay } from './LoadingOverlay';

interface VideoInspectorProps {
  onComplete: (videoUrl: string, timeline: CrackAnalysis[], videoName: string, evaluationDuration: number, fps: number, range: [number, number], metadata: any) => void;
  onCancel: () => void;
  toolSpecs?: string;
  toolFps?: number;
  toolId?: string;
  initialModes?: InspectionMode[];
  initialFile?: File;
  initialRange?: [number, number];
  initialTrajectoryPoints?: TelemetryPoint[];
  initialLasModelName?: string;
  initialModelPoints?: TelemetryPoint[];
  initialOverlap?: number;
  initialTimeline?: CrackAnalysis[];
  initialCloudOffset?: { x: number, y: number };
}

interface FlightSegment {
  id: string;
  video: File;
  videoUrl: string;
  trajectory: TelemetryPoint[];
  duration: number;
  pointCount: number;
}

const severityColors: Record<Severity, string> = {
  [Severity.NONE]: '#64748b',
  [Severity.LOW]: '#3b82f6',
  [Severity.MEDIUM]: '#eab308',
  [Severity.HIGH]: '#f97316',
  [Severity.CRITICAL]: '#ef4444',
};

const PROTOCOL_OPTIONS = [
  {
    id: InspectionMode.CRACK_ONLY,
    label: 'Crack Detection',
    icon: <svg className="w-10 h-10" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M13 10V3L4 14h7v7l9-11h-7z" /></svg>,
    desc: 'Cracks & deep scratches.'
  },
  {
    id: InspectionMode.WELD_INTEGRITY,
    label: 'Weld Integrity',
    icon: <svg className="w-10 h-10" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" /></svg>,
    desc: 'Junctions & HAZ audit.'
  },
  {
    id: InspectionMode.GENERAL_NDT,
    label: 'Structural Audit',
    icon: <svg className="w-10 h-10" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" /></svg>,
    desc: 'Cracks, fissures, corrosion & dents.'
  },
  {
    id: InspectionMode.FOULING_INSPECTION,
    label: 'Cleanliness',
    icon: <svg className="w-10 h-10" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M19.428 15.428a2 2 0 00-1.022-.547l-2.387-.477a6 6 0 00-3.86.517l-.691.31a2 2 0 01-1.782 0l-.691-.31a6 6 0 00-3.86-.517l-2.388.477a2 2 0 00-1.022.547l-.34.34a2 2 0 000 2.829l1.245 1.244a2 2 0 002.829 0l.144-.144a2 2 0 000-2.829l-1.245-1.244" /></svg>,
    desc: 'Residue & Scaling.'
  }
];

export const VideoInspectorT: React.FC<VideoInspectorProps> = ({
  onComplete, onCancel, toolSpecs, toolFps = 30, toolId, initialModes = [InspectionMode.GENERAL_NDT], initialFile, initialRange, initialTrajectoryPoints, initialLasModelName, initialModelPoints, initialOverlap, initialTimeline, initialCloudOffset
}) => {
  const [segments, setSegments] = useState<FlightSegment[]>([]);
  const [activeSegmentIndex, setActiveSegmentIndex] = useState<number>(0);
  const [pendingVideo, setPendingVideo] = useState<File | null>(initialFile || null);
  const [pendingTrajectory, setPendingTrajectory] = useState<TelemetryPoint[] | null>(initialTrajectoryPoints && initialTrajectoryPoints.length > 0 ? initialTrajectoryPoints : null);
  const [pendingTrajectoryFile, setPendingTrajectoryFile] = useState<File | null>(null);

  const currentSegment = segments[activeSegmentIndex];
  const videoUrl = currentSegment?.videoUrl || (initialFile ? URL.createObjectURL(initialFile) : null);
  const videoFile = currentSegment?.video || initialFile;
  const trajectoryPoints = currentSegment?.trajectory || initialTrajectoryPoints || null;

  const [isProcessing, setIsProcessing] = useState(false);
  const [isFinished, setIsFinished] = useState(false);
  const [inspectionModes, setInspectionModes] = useState<InspectionMode[]>(initialModes);
  const [progress, setProgress] = useState(0);
  const abortRef = useRef(false);
  const [isAborting, setIsAborting] = useState(false);
  const stopNeuralEngine = () => { abortRef.current = true; setIsAborting(true); };
  const [detectedFps, setDetectedFps] = useState<number>(30);
  const [elapsedClock, setElapsedClock] = useState<number>(0);
  const [finalTimeline, setFinalTimeline] = useState<CrackAnalysis[]>(initialTimeline || []);

  const [sensitivity, setSensitivity] = useState(5);
  const [resolutionMode, setResolutionMode] = useState<ImageResolutionMode>(ImageResolutionMode.STANDARD);
  // Fix: Default to initialOverlap if provided from external planner context
  const [forwardOverlap, setForwardOverlap] = useState(initialOverlap ?? 25);
  const [syncOffset, setSyncOffset] = useState(0);

  const [videoDuration, setVideoDuration] = useState(0);
  const [isArchiving, setIsArchiving] = useState(false);
  // Requirement: Manual inspector must always accept/verify staged files via the initiation screen
  // Bypasses initiation screen if initial data is provided from project resume context
  const [complianceAccepted, setComplianceAccepted] = useState(!!initialFile);

  const [rangeStart, setRangeStart] = useState(initialRange ? initialRange[0] : 0);
  const [rangeEnd, setRangeEnd] = useState(initialRange ? initialRange[1] : 0);
  const [currentTime, setCurrentTime] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);
  const [showCapOnly, setShowCapOnly] = useState(false);

  const [segmentRanges, setSegmentRanges] = useState<Record<string, [number, number]>>({});
  const [debouncedRange, setDebouncedRange] = useState<[number, number]>([rangeStart, rangeEnd]);
  const [debouncedTime, setDebouncedTime] = useState(currentTime);

  const [missionStartTime, setMissionStartTime] = useState<number | undefined>(initialTrajectoryPoints && initialTrajectoryPoints.length > 0 ? initialTrajectoryPoints[0].timestamp : undefined);
  const [lasModelName, setLasModelName] = useState<string | null>(initialLasModelName || null);
  const [modelPoints, setModelPoints] = useState<TelemetryPoint[]>(initialModelPoints || []);
  const isUAV = toolId === 'Elios3 UAV Chambers';

  const [activeFindings, setActiveFindings] = useState<Finding[]>([]);
  const [sessionFindings, setSessionFindings] = useState<CrackAnalysis[]>(initialTimeline || []);
  const [verifiedFindingsIdx, setVerifiedFindingsIdx] = useState<Set<number>>(new Set((initialTimeline || []).map((_, i) => i)));
  const [aiLog, setAiLog] = useState<{ msg: string, type: 'info' | 'warn' | 'crit' }[]>([]);
  const [linkQuality, setLinkQuality] = useState<number>(1.0);
  const [lastAuditStatus, setLastAuditStatus] = useState<"IDLE" | "SAMPLING" | "THINKING" | "RESOLVED" | "WAITING">("IDLE");
  const [detectedOrientation, setDetectedOrientation] = useState<'H' | 'V' | null>(null);
  const [isExportingZip, setIsExportingZip] = useState(false);

  // ─── Dedup state ───────────────────────────────────────────────────────────
  // Threshold in centimeters — two same-type findings within this distance on
  // the wall are treated as the same physical defect. 10cm is a reasonable
  // default for Elios 3 NDT at typical standoffs; tune per equipment.
  const [dedupThresholdCm, setDedupThresholdCm] = useState<number>(10);
  const [dedupMergeGroups, setDedupMergeGroups] = useState<MergeGroup[]>([]);
  const [dedupStats, setDedupStats] = useState<{ total: number; unique: number; duplicatesRemoved: number } | null>(null);
  const [showMergeReview, setShowMergeReview] = useState(false);

  // ─── Wiggle test displacement (cm) ───────────────────────────────────────
  // Target drone displacement between the 3 wiggle frames. Controls parallax
  // for the reflection-vs-defect test. Smaller = tighter inspection (defect
  // stays in frame, less reflection shift); larger = more parallax but
  // defect moves more across the image. Falls back to time-based (±1.5s)
  // when the trajectory can't provide the target displacement within ±3s.
  const [wiggleDisplacementCm, setWiggleDisplacementCm] = useState<number>(15);

  useEffect(() => {
    if (initialTimeline && initialTimeline.length > 0) {
      setSessionFindings(initialTimeline);
      setFinalTimeline(initialTimeline);
      setVerifiedFindingsIdx(new Set(initialTimeline.map((_, i) => i)));
    }
  }, [initialTimeline]);

  const videoRef = useRef<HTMLVideoElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const previewCanvasRef = useRef<HTMLCanvasElement>(null);
  const timerRef = useRef<number | null>(null);
  const rafRef = useRef<number | null>(null);
  const playbackIntervalRef = useRef<number | null>(null);
  const logContainerRef = useRef<HTMLDivElement>(null);
  const stageVideoRef = useRef<HTMLInputElement>(null);
  const stageCsvRef = useRef<HTMLInputElement>(null);
  const lasInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (trajectoryPoints && trajectoryPoints.length > 0) {
      const logStart = trajectoryPoints[0].timestamp;
      const missionStartVal = detectMissionStart(trajectoryPoints);
      const calculatedOffset = (missionStartVal - logStart) - 2.5;
      setSyncOffset(Number(calculatedOffset.toFixed(1)));
      setMissionStartTime(logStart);
    }
  }, [trajectoryPoints]);

  useEffect(() => {
    if (trajectoryPoints && trajectoryPoints.length > 0) {
      const orient = detectTrajectoryOrientation(trajectoryPoints);
      setDetectedOrientation(orient === 'HORIZONTAL' ? 'H' : orient === 'VERTICAL' ? 'V' : null);
    } else {
      setDetectedOrientation(null);
    }
  }, [trajectoryPoints]);

  useEffect(() => {
    setSegmentRanges(prev => {
      const next = { ...prev };
      let changed = false;
      segments.forEach(s => { if (!next[s.id]) { next[s.id] = [0, s.duration]; changed = true; } });
      return changed ? next : prev;
    });
  }, [segments]);

  const combinedTrajectoryData = useMemo(() => {
    if (segments.length > 0) {
      let timeOffset = 0;
      const allPoints: TelemetryPoint[] = [];
      const offsets: number[] = [];
      segments.forEach((seg) => {
        offsets.push(timeOffset);
        const sortedSegPoints = [...seg.trajectory].sort((a, b) => a.timestamp - b.timestamp);
        if (sortedSegPoints.length === 0) return;
        const firstT = sortedSegPoints[0].timestamp;
        const shifted = sortedSegPoints.map(p => ({ ...p, timestamp: (p.timestamp - firstT) + timeOffset }));
        allPoints.push(...shifted);
        const r = segmentRanges[seg.id] || [0, seg.duration];
        timeOffset += Math.max(0, r[1] - r[0]) + 5.0;
      });
      return { points: allPoints, offsets };
    }
    return { points: trajectoryPoints || [], offsets: [0] };
  }, [segments, trajectoryPoints, segmentRanges]);

  const combinedTrajectory = combinedTrajectoryData.points;
  const segmentOffsets = combinedTrajectoryData.offsets;

  // Asset geometry (vessel center + radius etc.) — computed from the trajectory.
  // Used by the wall-anchor projection for each finding. Memoized so the RANSAC
  // fit runs only when the trajectory changes, not on every render.
  const assetGeometry = useMemo(() => {
    if (!combinedTrajectory || combinedTrajectory.length < 50) return null;
    return calculateAssetGeometry(combinedTrajectory);
  }, [combinedTrajectory]);

  const currentGlobalMapTime = useMemo(() => {
    if (segments.length <= 1) return debouncedTime + syncOffset;
    const segmentOffset = combinedTrajectoryData.offsets[activeSegmentIndex] || 0;
    const range = segmentRanges[currentSegment?.id || ''] || [0, 0];
    return Math.max(0, debouncedTime - range[0]) + segmentOffset + syncOffset;
  }, [debouncedTime, syncOffset, segments.length, activeSegmentIndex, combinedTrajectoryData, segmentRanges, currentSegment]);

  const activeGlobalRange = useMemo(() => {
    if (segments.length <= 1) return [rangeStart + syncOffset, rangeEnd + syncOffset] as [number, number];
    const r = (currentSegment?.id ? segmentRanges[currentSegment.id] : null) || [0, currentSegment?.duration || 0];
    const o = segmentOffsets[activeSegmentIndex] || 0;
    return [o + r[0] + syncOffset, o + r[1] + syncOffset] as [number, number];
  }, [rangeStart, rangeEnd, syncOffset, activeSegmentIndex, segmentOffsets, segments.length, segmentRanges, currentSegment]);

  useEffect(() => { const h = setTimeout(() => { setDebouncedRange([rangeStart, rangeEnd]); }, 100); return () => clearTimeout(h); }, [rangeStart, rangeEnd]);
  useEffect(() => { const h = setTimeout(() => { setDebouncedTime(currentTime); }, 50); return () => clearTimeout(h); }, [currentTime]);

  const canProceed = useMemo(() => segments.length > 0 || (isUAV ? (!!videoFile && !!trajectoryPoints) : !!videoFile), [videoFile, trajectoryPoints, isUAV, segments]);
  const toggleMode = (mode: InspectionMode) => setInspectionModes(prev => prev.includes(mode) ? (prev.length === 1 ? prev : prev.filter(m => m !== mode)) : [...prev, mode]);

  const formatTime = (seconds: number) => {
    const s = Math.max(0, Math.round(seconds)), m = Math.floor(s / 60);
    return `${m.toString().padStart(2, '0')}:${(s % 60).toString().padStart(2, '0')}`;
  };

  useEffect(() => {
    if (initialFile) {
      const url = URL.createObjectURL(initialFile);
      setSegments([{ id: 'initial-transfer', video: initialFile, videoUrl: url, trajectory: initialTrajectoryPoints || [], duration: 0, pointCount: initialTrajectoryPoints?.length || 0 }]);
      setComplianceAccepted(true);
    }
  }, [initialFile, initialTrajectoryPoints]);

  const handleMetadataLoaded = () => {
    if (videoRef.current) {
      setVideoDuration(videoRef.current.duration);
      if (activeSegmentIndex === 0 && (!initialRange || initialRange[1] === 0)) setRangeEnd(videoRef.current.duration);
      videoRef.current.currentTime = rangeStart;
      setCurrentTime(rangeStart);
      setTimeout(drawPreviewFrame, 200);
    }
  };

  const drawPreviewFrame = useCallback(() => {
    const video = videoRef.current, canvas = previewCanvasRef.current;
    if (!video || !canvas) return;
    const ctx = canvas.getContext('2d', { alpha: false });
    if (!ctx) return;
    if (canvas.width !== video.videoWidth || canvas.height !== video.videoHeight) { if (video.videoWidth > 0 && video.videoHeight > 0) { canvas.width = video.videoWidth; canvas.height = video.videoHeight; } }
    ctx.fillStyle = '#000000'; ctx.fillRect(0, 0, canvas.width, canvas.height);
    if (video.readyState >= 2) ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
    const scale = Math.max(1, canvas.width / 1280);
    if (isProcessing && activeFindings.length > 0) {
      activeFindings.forEach((finding, idx) => {
        const [ymin, xmin, ymax, xmax] = finding.box, color = severityColors[finding.severity] || '#fff';
        const x = (xmin / 1000) * canvas.width, y = (ymin / 1000) * canvas.height, w = ((xmax - xmin) / 1000) * canvas.width, h = ((ymax - ymin) / 1000) * canvas.height;
        ctx.strokeStyle = color; ctx.lineWidth = 4 * scale; ctx.strokeRect(x, y, w, h);
        ctx.fillStyle = color; ctx.font = `bold ${Math.max(12, 16 * scale)}px JetBrains Mono`;
        ctx.fillText(`ANOMALY: ${finding.severity.toUpperCase()}`, x, y - (10 * scale));

        // Add corner brackets for extra premium look
        const cornerSize = 20 * scale;
        ctx.beginPath();
        ctx.moveTo(x, y + cornerSize); ctx.lineTo(x, y); ctx.lineTo(x + cornerSize, y);
        ctx.moveTo(x + w - cornerSize, y); ctx.lineTo(x + w, y); ctx.lineTo(x + w, y + cornerSize);
        ctx.moveTo(x + w, y + h - cornerSize); ctx.lineTo(x + w, y + h); ctx.lineTo(x + w - cornerSize, y + h);
        ctx.moveTo(x + cornerSize, y + h); ctx.lineTo(x, y + h); ctx.lineTo(x, y + h - cornerSize);
        ctx.stroke();
      });
    }

    if (isProcessing) {
      const padding = 20 * scale, startX = 20 * scale, startY = 20 * scale, headerSize = Math.floor(16 * scale), bodySize = Math.floor(11 * scale), lineHeight = Math.floor(19 * scale);
      const boxWidth = Math.max(280, 420 * scale), boxHeight = (padding * 2) + headerSize + (5 * lineHeight);
      ctx.fillStyle = 'rgba(0, 0, 0, 0.7)'; ctx.fillRect(startX, startY, boxWidth, boxHeight);
      ctx.fillStyle = '#06b6d4'; ctx.font = `bold ${headerSize}px JetBrains Mono`; ctx.fillText(`V6.0 MISSION HUD | FLIGHT ${activeSegmentIndex + 1}`, startX + padding, startY + padding + headerSize);
      ctx.fillStyle = '#ffffff'; ctx.font = `bold ${bodySize}px JetBrains Mono`;
      let cursorY = startY + padding + headerSize + lineHeight;
      ctx.fillText(`FPS CALIBRATION: ${detectedFps}`, startX + padding, cursorY); cursorY += lineHeight;
      ctx.fillText(`SENSITIVITY: ${sensitivity}/10 | FWD OVERLAP (T): ${forwardOverlap}%`, startX + padding, cursorY); cursorY += lineHeight;
      ctx.fillText(`AUDIT STATUS: ${lastAuditStatus}`, startX + padding, cursorY); cursorY += lineHeight;
      const label = `LINK STABILIZER:`; ctx.fillText(label, startX + padding, cursorY);
      const labelWidth = ctx.measureText(label).width, barW = boxWidth - (padding * 2) - labelWidth - (15 * scale), barH = 8 * scale, barY = cursorY - (bodySize / 2) - (barH / 2);
      ctx.fillStyle = linkQuality > 0.8 ? '#4ade80' : linkQuality > 0.4 ? '#fbbf24' : '#f87171'; ctx.fillRect(startX + padding + labelWidth + (15 * scale), barY, barW * linkQuality, barH); ctx.strokeStyle = 'rgba(255, 255, 255, 0.3)'; ctx.lineWidth = 1 * scale; ctx.strokeRect(startX + padding + labelWidth + (15 * scale), barY, barW, barH);
      cursorY += lineHeight; ctx.fillStyle = activeFindings.length > 0 ? '#f87171' : '#4ade80'; ctx.fillText(`THREAT INDEX: ${activeFindings.length > 0 ? 'ANOMALIES DETECTED' : 'NOMINAL'}`, startX + padding, cursorY);
      const scanY = (Date.now() % 4000 / 4000) * canvas.height; ctx.shadowBlur = 20 * scale; ctx.shadowColor = '#22c55e'; ctx.strokeStyle = '#22c55e'; ctx.lineWidth = 6 * scale; ctx.beginPath(); ctx.moveTo(0, scanY); ctx.lineTo(canvas.width, scanY); ctx.stroke(); ctx.shadowBlur = 0;

      // TOP RIGHT ORIENTATION INDICATOR (H / V)
      if (detectedOrientation) {
        const orientSize = 100 * scale;
        const orientX = canvas.width - orientSize - (20 * scale);
        const orientY = 20 * scale;

        ctx.fillStyle = 'rgba(0, 0, 0, 0.8)';
        ctx.fillRect(orientX, orientY, orientSize, orientSize);
        ctx.strokeStyle = '#06b6d4';
        ctx.lineWidth = 3 * scale;
        ctx.strokeRect(orientX, orientY, orientSize, orientSize);

        ctx.beginPath();
        if (detectedOrientation === 'V') {
          // Double-headed Vertical Arrow
          const centerX = orientX + (orientSize / 2);
          const topY = orientY + (20 * scale);
          const bottomY = orientY + orientSize - (20 * scale);
          const headSize = 12 * scale;
          ctx.moveTo(centerX, topY); ctx.lineTo(centerX, bottomY);
          ctx.moveTo(centerX - headSize, topY + headSize); ctx.lineTo(centerX, topY); ctx.lineTo(centerX + headSize, topY + headSize); // Top head
          ctx.moveTo(centerX - headSize, bottomY - headSize); ctx.lineTo(centerX, bottomY); ctx.lineTo(centerX + headSize, bottomY - headSize); // Bottom head
        } else {
          // Double-headed Horizontal Arrow
          const centerY = orientY + (orientSize / 2);
          const leftX = orientX + (20 * scale);
          const rightX = orientX + orientSize - (20 * scale);
          const headSize = 12 * scale;
          ctx.moveTo(leftX, centerY); ctx.lineTo(rightX, centerY);
          ctx.moveTo(leftX + headSize, centerY - headSize); ctx.lineTo(leftX, centerY); ctx.lineTo(leftX + headSize, centerY + headSize); // Left head
          ctx.moveTo(rightX - headSize, centerY - headSize); ctx.lineTo(rightX, centerY); ctx.lineTo(rightX - headSize, centerY + headSize); // Right head
        }
        ctx.stroke();
      }
    }
  }, [isProcessing, activeFindings, sensitivity, forwardOverlap, detectedFps, lastAuditStatus, linkQuality, activeSegmentIndex, detectedOrientation]);

  useEffect(() => { const updateLoop = () => { drawPreviewFrame(); rafRef.current = requestAnimationFrame(updateLoop); }; rafRef.current = requestAnimationFrame(updateLoop); return () => { if (rafRef.current) cancelAnimationFrame(rafRef.current); }; }, [drawPreviewFrame]);

  useEffect(() => {
    if (isPlaying) {
      playbackIntervalRef.current = window.setInterval(() => {
        if (videoRef.current) {
          const t = videoRef.current.currentTime;
          if (t >= rangeEnd) { togglePlayback(false); videoRef.current.currentTime = rangeEnd; setCurrentTime(rangeEnd); }
          else { setCurrentTime(t); setDebouncedTime(t); }
        }
      }, 50);
    } else { if (playbackIntervalRef.current) clearInterval(playbackIntervalRef.current); }
    return () => { if (playbackIntervalRef.current) clearInterval(playbackIntervalRef.current); }
  }, [isPlaying, rangeEnd]);

  useEffect(() => { if (videoRef.current && currentSegment && !isProcessing) { const vid = videoRef.current, range = segmentRanges[currentSegment.id] || [0, currentSegment.duration]; vid.currentTime = range[0]; setTimeout(drawPreviewFrame, 100); } }, [activeSegmentIndex, currentSegment, isProcessing, segmentRanges]);

  const handleRangeChange = (e: React.ChangeEvent<HTMLInputElement>, isStart: boolean) => {
    const val = parseFloat(e.target.value), video = videoRef.current; if (!video) return;
    if (!video.paused) { video.pause(); setIsPlaying(false); }
    if (isStart) { const f = Math.min(val, rangeEnd - 0.1); setRangeStart(f); video.currentTime = f; setCurrentTime(f); }
    else { const f = Math.max(val, rangeStart + 0.1); setRangeEnd(f); video.currentTime = f; setCurrentTime(f); }
  };

  const togglePlayback = (forceState?: boolean) => { const video = videoRef.current; if (!video) return; const shouldPlay = forceState !== undefined ? forceState : !isPlaying; if (shouldPlay) { if (video.currentTime >= rangeEnd) video.currentTime = rangeStart; video.play().catch(e => console.error(e)); setIsPlaying(true); } else { video.pause(); setIsPlaying(false); } };
  const stopPlayback = () => { const video = videoRef.current; if (!video) return; video.pause(); video.currentTime = rangeStart; setCurrentTime(rangeStart); setIsPlaying(false); };
  const stepFrame = (frames: number) => { const video = videoRef.current; if (!video) return; video.pause(); setIsPlaying(false); const fTime = 1 / 30, newTime = Math.min(rangeEnd, Math.max(rangeStart, video.currentTime + (frames * fTime))); video.currentTime = newTime; setCurrentTime(newTime); };

  const handleStageVideo = (e: React.ChangeEvent<HTMLInputElement>) => { const file = e.target.files?.[0]; if (file) setPendingVideo(file); };
  const handleStageCsv = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setPendingTrajectoryFile(file);
    const reader = new FileReader();
    reader.onload = (event) => {
      const pts = parseTrajectoryCsv(event.target?.result as string);
      if (pts.length > 0) setPendingTrajectory(pts);
      else alert("Invalid Telemetry Data.");
    };
    reader.readAsText(file);
  };

  const handleLasUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setLasModelName(file.name);
      setIsArchiving(true);
      let pts: TelemetryPoint[] = [];
      try {
        // Auto-save PointCloud to Local Project
        await saveFileToProject('04_Telemetry_Data', file.name, file);

        const ext = file.name.toLowerCase().split('.').pop();
        if (ext === 'las' || ext === 'laz') pts = await parseLasFile(file); else pts = await parseTextPointCloud(file);

        if (pts.length > 0) {
          const geom = calculateAssetGeometry(pts);
          const filtered = filterPointsToAssetBoundary(pts, geom, 0.5);
          setModelPoints(filtered);
        }
      } catch (e) { alert("Failed to import point cloud."); }
      finally {
        setIsArchiving(false);
      }
      // Save checkpoint for resumption
      saveSessionCheckpoint({
        lasModelName: file.name,
        modelPoints: pts,
        view: 'VIDEO_INSPECTION_T',
        modes: inspectionModes
      });
    }
  };

  const addFlightSegment = async () => {
    if (pendingVideo && (pendingTrajectory || !isUAV)) {
      const videoFile = pendingVideo;
      const csvFile = pendingTrajectoryFile;
      const url = URL.createObjectURL(videoFile);

      setIsArchiving(true);
      try {
        // Auto-save assets to Local Project (V6.1 Schema)
        await saveFileToProject('03_Video_Logs', videoFile.name, videoFile);
        if (csvFile) {
          await saveFileToProject('04_Telemetry_Data', csvFile.name, csvFile);
        }

        // Save session checkpoint
        saveSessionCheckpoint({
          videoFile,
          trajectory: pendingTrajectory,
          range: [rangeStart, rangeEnd],
          view: 'VIDEO_INSPECTION_T',
          modes: inspectionModes
        });
      } finally {
        setIsArchiving(false);
      }

      const temp = document.createElement('video');
      temp.src = url;
      temp.onloadedmetadata = () => {
        setSegments(prev => [...prev, {
          id: crypto.randomUUID(),
          video: videoFile,
          videoUrl: url,
          trajectory: pendingTrajectory || [],
          duration: temp.duration,
          pointCount: pendingTrajectory?.length || 0
        }]);
        setPendingVideo(null);
        setPendingTrajectory(null);
        setPendingTrajectoryFile(null);
        if (stageVideoRef.current) stageVideoRef.current.value = "";
        if (stageCsvRef.current) stageCsvRef.current.value = "";
      };
    }
  };
  const removeSegment = (id: string) => setSegments(prev => prev.filter(s => s.id !== id));

  const seekToTime = (v: HTMLVideoElement, t: number): Promise<void> => new Promise(res => { const target = Math.max(0, Math.min(t, v.duration)), onS = () => { v.removeEventListener('seeked', onS); res(); }; v.addEventListener('seeked', onS); v.currentTime = target; });
  const captureFrameForAnalysis = (v: HTMLVideoElement, c: HTMLCanvasElement): string | null => {
    const ctx = c.getContext('2d');
    if (!ctx || v.videoWidth === 0) return null;

    // Respect selected resolution mode for output frames
    const targetRes = IMAGE_RESOLUTION_OPTIONS.find(o => o.mode === resolutionMode) || IMAGE_RESOLUTION_OPTIONS[0];
    const targetWidth = Math.min(v.videoWidth, targetRes.px);
    const ratio = targetWidth / v.videoWidth;
    const targetHeight = Math.round(v.videoHeight * ratio);

    c.width = targetWidth;
    c.height = targetHeight;
    ctx.drawImage(v, 0, 0, c.width, c.height);
    return c.toDataURL('image/jpeg', 0.92);
  };

  const getSpatialDataAtTime = (time: number, pts: TelemetryPoint[], logOffset: number) => {
    if (!pts || pts.length === 0) return null;
    const absTarget = time + syncOffset + logOffset;
    let closest = pts[0], minD = Infinity;
    for (const p of pts) { const d = Math.abs(p.timestamp - absTarget); if (d < minD) { minD = d; closest = p; } }
    const dx = closest.x - 0, dy = closest.y - 0, az = (Math.atan2(dy, dx) * 180 / Math.PI);
    return { coords: { x: closest.x, y: closest.y, z: closest.z }, azimuth: az < 0 ? az + 360 : az };
  };

  // Same nearest-neighbour lookup as getSpatialDataAtTime but returns the FULL
  // pose including quaternion — needed to project bbox centers onto the wall.
  const getFullPoseAtTime = (time: number, pts: TelemetryPoint[], logOffset: number): TelemetryPoint | null => {
    if (!pts || pts.length === 0) return null;
    const absTarget = time + syncOffset + logOffset;
    let closest = pts[0], minD = Infinity;
    for (const p of pts) { const d = Math.abs(p.timestamp - absTarget); if (d < minD) { minD = d; closest = p; } }
    return closest;
  };

  // Walks the trajectory forward/backward from the target time until the drone
  // has moved `targetMeters` through space. Returns a VIDEO-RELATIVE time
  // (same domain as `time` argument), or null if the target displacement can't
  // be reached within ±maxTimeDelta seconds (drone hovering / start/end of clip).
  //
  // Used to pick wiggle-test neighbor frames with a consistent parallax baseline
  // regardless of drone speed.
  const findTimeByDisplacement = (
    time: number,
    pts: TelemetryPoint[],
    logOffset: number,
    targetMeters: number,
    direction: 'forward' | 'backward',
    maxTimeDelta: number = 3.0
  ): number | null => {
    if (!pts || pts.length === 0) return null;
    const absTarget = time + syncOffset + logOffset;

    // Find nearest trajectory point to target time
    let startIdx = 0, minD = Infinity;
    for (let i = 0; i < pts.length; i++) {
      const d = Math.abs(pts[i].timestamp - absTarget);
      if (d < minD) { minD = d; startIdx = i; }
    }
    const startP = pts[startIdx];

    // Walk in the requested direction
    const stepDir = direction === 'forward' ? 1 : -1;
    const maxAbs = absTarget + (direction === 'forward' ? maxTimeDelta : -maxTimeDelta);

    let i = startIdx + stepDir;
    while (i >= 0 && i < pts.length) {
      const p = pts[i];
      // Stop if we've walked too far in time
      if (direction === 'forward' && p.timestamp > maxAbs) return null;
      if (direction === 'backward' && p.timestamp < maxAbs) return null;

      const dx = p.x - startP.x, dy = p.y - startP.y, dz = p.z - startP.z;
      const dist = Math.sqrt(dx * dx + dy * dy + dz * dz);
      if (dist >= targetMeters) {
        // Found it — convert trajectory absolute time back to video-relative time
        return p.timestamp - syncOffset - logOffset;
      }
      i += stepDir;
    }
    return null; // Reached start/end of trajectory before hitting target displacement
  };

  const removeFinding = (idx: number) => {
    setSessionFindings(prev => prev.filter((_, i) => i !== idx));
    setVerifiedFindingsIdx(prev => {
      const next = new Set<number>();
      prev.forEach(v => { if (v > idx) next.add(v - 1); else if (v < idx) next.add(v); });
      return next;
    });
  };

  const verifyFinding = async (idx: number) => {
    const analysis = sessionFindings[idx];
    const isAdding = !verifiedFindingsIdx.has(idx);

    setVerifiedFindingsIdx(prev => {
      const next = new Set(prev);
      if (next.has(idx)) next.delete(idx); else next.add(idx);
      return next;
    });

    if (isAdding && analysis.frameImage) {
      // INSTANT PERSISTENCE: Save to Verified (02_) and Ground Truth (06_)
      await saveEntryToVault({
        image: analysis.frameImage,
        timestamp: analysis.timestamp || Date.now(),
        analysis: analysis,
        isVerified: true
      });
    }
  };

  const runDeepAnalysis = async (append: boolean = false) => {
    if (!videoRef.current || !canvasRef.current || segments.length === 0) return;
    abortRef.current = false;
    setIsAborting(false);
    stopPlayback(); setIsProcessing(true); setIsFinished(false); setProgress(0);
    if (!append) {
      setSessionFindings([]); setFinalTimeline([]);
    }
    setAiLog([{ msg: ">>> INITIALIZING NEURAL AUDIT ENGINE V6.0...", type: 'info' }]);
    const video = videoRef.current, canvas = canvasRef.current;
    const timeline: CrackAnalysis[] = [], temporalOffset = Math.max(0.04, 2.0 / detectedFps);
    const startTimer = Date.now(); timerRef.current = window.setInterval(() => setElapsedClock((Date.now() - startTimer) / 1000), 100);

    for (let segIdx = 0; segIdx < segments.length; segIdx++) {
      if (abortRef.current) break;
      const seg = segments[segIdx]; setActiveSegmentIndex(segIdx);
      if (videoRef.current.src !== seg.videoUrl) { videoRef.current.src = seg.videoUrl; await new Promise(res => { videoRef.current!.onloadedmetadata = res; }); }
      const segStartLogTime = seg.trajectory.length > 0 ? seg.trajectory[0].timestamp : 0;
      let scanTimestamps: number[] = [], tStart = (segments.length === 1) ? rangeStart : (segmentRanges[seg.id]?.[0] || 0), tEnd = (segments.length === 1) ? rangeEnd : (segmentRanges[seg.id]?.[1] || seg.duration);
      const step = Math.max(0.05, 4.0 * (1 - forwardOverlap / 100));
      for (let t = tStart; t < tEnd; t += step) scanTimestamps.push(t);

      for (let i = 0; i < scanTimestamps.length; i++) {
        if (abortRef.current) break;
        const targetTime = scanTimestamps[i]; setCurrentTime(targetTime); setLastAuditStatus("SAMPLING"); setProgress(((segIdx / segments.length) * 100) + ((i / scanTimestamps.length) * (100 / segments.length)));
        // Capture context frames (±5s) FIRST, then return to targetTime for HUD display.
        // HUD stays at targetTime during the entire detection call.
        //
        // Wiggle test frame selection — use drone DISPLACEMENT instead of fixed time gap.
        // Gives consistent parallax baseline regardless of flight speed. Falls back
        // to time-based (±1.5s) if trajectory can't provide the target displacement
        // within ±3s (drone hovering, start/end of clip, or no trajectory).
        const wiggleMeters = wiggleDisplacementCm / 100;
        const timeFallbackGap = Math.min(1.5, (tEnd - tStart) * 0.10);

        let prevTime = findTimeByDisplacement(targetTime, seg.trajectory, segStartLogTime, wiggleMeters, 'backward');
        let nextTime = findTimeByDisplacement(targetTime, seg.trajectory, segStartLogTime, wiggleMeters, 'forward');

        let wiggleMode: 'displacement' | 'time-fallback' = 'displacement';
        if (prevTime === null || nextTime === null) {
          wiggleMode = 'time-fallback';
          prevTime = targetTime - timeFallbackGap;
          nextTime = targetTime + timeFallbackGap;
        }

        // Clamp to video bounds
        prevTime = Math.max(tStart, prevTime);
        nextTime = Math.min(tEnd, nextTime);

        // Diagnostic — runs once per frame. Strip once verified.
        // Computes approximate drone speed in m/s at the target frame for sanity checking.
        {
          const trajNow = getFullPoseAtTime(targetTime, seg.trajectory, segStartLogTime);
          const trajPrev = getFullPoseAtTime(targetTime - 0.5, seg.trajectory, segStartLogTime);
          let speedMps: number | null = null;
          if (trajNow && trajPrev && trajNow !== trajPrev) {
            const dt = Math.abs(trajNow.timestamp - trajPrev.timestamp);
            if (dt > 0.01) {
              const dx = trajNow.x - trajPrev.x, dy = trajNow.y - trajPrev.y, dz = trajNow.z - trajPrev.z;
              speedMps = Math.sqrt(dx * dx + dy * dy + dz * dz) / dt;
            }
          }
          console.log('[WIGGLE-PICK]', {
            targetTime: targetTime.toFixed(2),
            prevTime: prevTime.toFixed(2),
            nextTime: nextTime.toFixed(2),
            gapPrevSec: (targetTime - prevTime).toFixed(2),
            gapNextSec: (nextTime - targetTime).toFixed(2),
            targetDisplacementCm: wiggleDisplacementCm,
            mode: wiggleMode,
            droneSpeedMps: speedMps !== null ? speedMps.toFixed(3) : 'unknown',
          });
        }

        await seekToTime(video, prevTime); const framePrev = captureFrameForAnalysis(video, canvas);
        await seekToTime(video, nextTime); const frameNext = captureFrameForAnalysis(video, canvas);
        await seekToTime(video, targetTime); const frameCurr = captureFrameForAnalysis(video, canvas); // HUD now correct
        if (framePrev && frameCurr && frameNext) {
          setLastAuditStatus("THINKING");
          try {
            const orientation = detectTrajectoryOrientation(seg.trajectory);
            const analysis = await analyzeTwoPass(framePrev, frameCurr, frameNext, inspectionModes, toolSpecs, sensitivity, 6000, undefined, orientation, resolutionMode);
            const spatial = getSpatialDataAtTime(targetTime, seg.trajectory, segStartLogTime);
            // Full drone pose (position + quaternion) at the frame time — needed
            // for the per-finding wall-anchor projection.
            const fullPose = getFullPoseAtTime(targetTime, seg.trajectory, segStartLogTime);

            // Diagnostic — runs once per frame. Confirms whether wall-anchor projection
            // has the inputs it needs. Remove after projection is verified.
            if ((analysis.findings || []).length > 0) {
              const f0 = analysis.findings[0];
              const wa = assetGeometry ? computeWallAnchor(f0, fullPose, modelPoints, assetGeometry.center, assetGeometry.radius) : null;
              console.log('[ANCHOR-DEBUG]', {
                hasAssetGeometry: !!assetGeometry,
                assetGeometryRadius: assetGeometry?.radius,
                assetGeometryCenter: assetGeometry?.center,
                hasFullPose: !!fullPose,
                fullPoseSample: fullPose ? { x: fullPose.x?.toFixed(2), y: fullPose.y?.toFixed(2), z: fullPose.z?.toFixed(2), qw: fullPose.qw?.toFixed(2) } : null,
                hasModelPoints: (modelPoints || []).length,
                firstFindingHasBox: !!f0.box,
                firstFindingBoxLen: (f0.box || []).length,
                firstFindingBoxSample: f0.box,
                firstFindingType: f0.type,
                computedWallAnchor: wa,
              });
            }

            const enrichedFindings = (analysis.findings || []).map(f => {
              const wallAnchor = assetGeometry
                ? computeWallAnchor(
                    f,
                    fullPose,
                    modelPoints,
                    assetGeometry.center,
                    assetGeometry.radius
                  )
                : null;
              return {
                ...f,
                timestamp: targetTime,
                absoluteTime: targetTime,
                spatialCoords: spatial?.coords,
                azimuth: spatial?.azimuth,
                wallAnchor: wallAnchor || undefined,
              };
            });
            const enrichedAnalysis = { ...analysis, findings: enrichedFindings, timestamp: targetTime, frameImage: frameCurr, videoName: seg.video.name, mode: analysis.mode || inspectionModes[0] };
            if (enrichedFindings.length > 0) {
              setActiveFindings(enrichedFindings);
              setSessionFindings(prev => [...prev, enrichedAnalysis]);

              // Auto-save finding evidence to Local Project
              if (frameCurr) {
                const base64Data = frameCurr.split(',')[1];
                const byteCharacters = atob(base64Data);
                const byteNumbers = new Array(byteCharacters.length);
                for (let i = 0; i < byteCharacters.length; i++) {
                  byteNumbers[i] = byteCharacters.charCodeAt(i);
                }
                const byteArray = new Uint8Array(byteNumbers);
                const blob = new Blob([byteArray], { type: 'image/jpeg' });
                const baseName = `finding_${Math.floor(targetTime)}s_${seg.video.name}`;
                await saveFileToProject('01_Evidence_Images', `${baseName}.jpg`, blob);

                const metaBlob = new Blob([JSON.stringify({
                  ...enrichedAnalysis,
                  id: `ev-${Date.now()}-${Math.floor(targetTime)}`,
                  projectId: 'local'
                }, null, 2)], { type: 'application/json' });
                await saveFileToProject('01_Evidence_Images', `${baseName}.json`, metaBlob);
              }

              await new Promise(r => setTimeout(r, 2000));
              setActiveFindings([]);

              // PERSISTENCE POINT: Save intermediate session progress
              saveSessionCheckpoint({
                videoFile: segments.length > 0 ? segments[0].video : videoFile,
                trajectory: combinedTrajectory,
                range: [rangeStart, rangeEnd],
                view: 'VIDEO_INSPECTION_T',
                modes: inspectionModes,
                timeline: [...finalTimeline, ...timeline, enrichedAnalysis],
                lasModelName,
                modelPoints
              });
            }
            timeline.push(enrichedAnalysis); setLastAuditStatus("RESOLVED");
          } catch (err) { setLastAuditStatus("WAITING"); await new Promise(r => setTimeout(r, 2000)); }
        }
      }
    }
    if (timerRef.current) clearInterval(timerRef.current);
    setIsProcessing(false);
    setIsAborting(false);
    setIsFinished(true);
    const updatedTimeline = append ? [...finalTimeline, ...timeline] : timeline;
    setFinalTimeline(updatedTimeline);

    // ─── DEDUP PASS ────────────────────────────────────────────────────────
    // Flatten all findings from all analyses, dedup in world space, then apply
    // the result back to sessionFindings. Each CrackAnalysis keeps only the
    // findings that survived dedup (some may become empty — that's fine, those
    // are detections whose image was fully represented by another frame).
    try {
      const allFindings: any[] = [];
      updatedTimeline.forEach((analysis, aIdx) => {
        (analysis.findings || []).forEach((f: any, fIdx) => {
          allFindings.push({ ...f, __analysisIdx: aIdx, __findingIdx: fIdx });
        });
      });
      const dedupResult = deduplicateFindings(allFindings, dedupThresholdCm);

      // Build a set of (analysisIdx|findingIdx) keys that SURVIVED
      const survivorKeys = new Set<string>();
      dedupResult.deduplicated.forEach((f: any) => {
        if (f.__analysisIdx != null && f.__findingIdx != null) {
          survivorKeys.add(`${f.__analysisIdx}|${f.__findingIdx}`);
        }
      });

      // Rebuild the timeline with only surviving findings per analysis
      const dedupTimeline = updatedTimeline.map((analysis, aIdx) => ({
        ...analysis,
        findings: (analysis.findings || []).filter((_: any, fIdx: number) =>
          survivorKeys.has(`${aIdx}|${fIdx}`)
        ),
      }));

      setFinalTimeline(dedupTimeline);
      setSessionFindings(dedupTimeline);
      setDedupStats(dedupResult.stats);
      setDedupMergeGroups(dedupResult.mergeGroups);

      console.log(`[DEDUP] ${dedupResult.stats.duplicatesRemoved} duplicates removed (${dedupResult.stats.total} → ${dedupResult.stats.unique}) at ${dedupThresholdCm}cm threshold.`);
    } catch (e) {
      console.warn('[DEDUP] Deduplication pass failed — keeping all findings:', e);
    }

    // PERSISTENCE POINT: Save session checkpoint when analysis completes
    saveSessionCheckpoint({
      videoFile: segments.length > 0 ? segments[0].video : videoFile,
      trajectory: combinedTrajectory,
      range: [rangeStart, rangeEnd],
      view: 'VIDEO_INSPECTION_T',
      modes: inspectionModes,
      timeline: updatedTimeline,
      lasModelName,
      modelPoints
    });
  };

  const handleZipDownload = async () => { if (sessionFindings.length === 0) return; setIsExportingZip(true); try { const allFindings: Finding[] = sessionFindings.flatMap(analysis => (analysis.findings || []).map(f => ({ ...f, frameImage: analysis.frameImage }))); await exportFindingsToZip(allFindings, `Video_Audit_Evidence`); } catch (e) { alert("Failed to generate ZIP."); } finally { setIsExportingZip(false); } };
  const handleContinue = () => {
    const finalVideo = segments.length > 0 ? segments[0].video : videoFile;
    let posterFrame = finalTimeline.length > 0 ? finalTimeline[0].frameImage : "";
    if (!posterFrame && videoRef.current && canvasRef.current) posterFrame = captureFrameForAnalysis(videoRef.current, canvasRef.current) || "";

    // Build a set of timestamps from verified sessionFindings for O(1) lookup
    // Using index directly is the safest — avoids floating-point timestamp equality issues
    const verifiedTimestamps = new Set<number>();
    sessionFindings.forEach((sf, idx) => {
      if (verifiedFindingsIdx.has(idx)) {
        verifiedTimestamps.add(sf.timestamp);
      }
    });

    // Keep all findings from analyses whose timestamp was verified
    // Fall back to entire finalTimeline if nothing was explicitly verified
    let verifiedTimeline = finalTimeline.filter(t => verifiedTimestamps.has(t.timestamp));
    if (verifiedTimeline.length === 0) {
      // Nothing marked verified — pass everything (user may not have used verification UI)
      verifiedTimeline = finalTimeline;
    }

    onComplete(videoUrl || "", verifiedTimeline, finalVideo?.name || "Multi-Flight Mission", elapsedClock, detectedFps, [rangeStart, rangeEnd], { posterFrame, file: finalVideo, trajectoryPoints: combinedTrajectory, lasModelName, modelPoints });
  };
  const handleAbort = () => { stopPlayback(); onCancel(); };

  return (
    <div className="flex flex-col h-full bg-slate-300">
      <div className="p-4 border-b border-slate-500 flex justify-between items-center bg-slate-400 shadow-sm">
        <div className="flex flex-col text-left">
          <div className="flex items-center gap-2">
            <div className={`w-2.5 h-2.5 rounded-none ${isProcessing ? 'bg-red-500 animate-pulse' : 'bg-[#0000a9] shadow-[0_0_10px_rgba(0,0,169,0.3)]'}`}></div>
            <h3 className="font-black text-slate-900 uppercase tracking-widest text-[10px] italic">MISSION CONTROL <span className="text-[#0000a9] not-italic mx-1">|</span> TELEMETRY AUDIT (INSPECTOR T)</h3>
          </div>
        </div>
        <button onClick={handleAbort} className="text-slate-700 hover:text-[#0000a9] transition uppercase text-[10px] font-black tracking-widest bg-slate-300 px-6 py-2 border border-slate-400 shadow-sm"> {complianceAccepted ? "Close Mission" : "Abort Mission"} </button>
      </div>

      <div className="flex-1 overflow-hidden flex flex-col p-6 bg-slate-300 relative">
        {videoUrl && complianceAccepted ? (
          <>
            <div className="flex-1 min-h-0 flex w-full gap-6">
              <video ref={videoRef} src={videoUrl} onLoadedMetadata={handleMetadataLoaded} onSeeked={() => drawPreviewFrame()} preload="auto" className="hidden" muted playsInline />
              <div className="flex-1 relative flex items-center justify-center overflow-hidden bg-slate-400 border border-slate-500 shadow-xl">
                <canvas ref={previewCanvasRef} className={`max-h-full max-w-full ${isProcessing ? 'opacity-90' : 'opacity-100'}`} />
                {isProcessing && (
                  <div className="absolute bottom-6 left-6 right-6 flex items-end justify-between pointer-events-none">
                    <div className="bg-slate-900/95 backdrop-blur-md border border-[#0000a9]/40 p-5 shadow-2xl text-left pointer-events-auto w-72 rounded-none">
                      <div className="flex items-center justify-between mb-3">
                        <span className="text-[9px] font-black text-slate-400 uppercase tracking-[0.25em]">Audit Progress</span>
                        <div className="flex items-center gap-1.5">
                          <div className="w-1.5 h-1.5 rounded-none bg-slate-400 animate-pulse" />
                          <span className="text-[8px] font-mono text-slate-300 uppercase">Neural Active</span>
                        </div>
                      </div>
                      <span className="text-3xl font-black italic text-white block leading-none mb-1">{Math.floor(progress)}%</span>
                      <div className="w-full h-1.5 bg-slate-700 overflow-hidden mt-2 mb-3">
                        <div className="h-full bg-[#0000a9] transition-all duration-300 relative overflow-hidden" style={{ width: `${progress}%` }}>
                          <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent animate-shimmer" />
                        </div>
                      </div>
                      <div className="grid grid-cols-2 gap-x-4 gap-y-1.5 border-t border-slate-700 pt-3">
                        <div>
                          <div className="text-[7px] text-slate-500 uppercase tracking-widest font-bold">Elapsed</div>
                          <div className="text-[11px] font-black text-slate-200 font-mono">{Math.floor(elapsedClock / 60).toString().padStart(2, '0')}:{Math.floor(elapsedClock % 60).toString().padStart(2, '0')}</div>
                        </div>
                        <div>
                          <div className="text-[7px] text-slate-500 uppercase tracking-widest font-bold">Time Left</div>
                          <div className="text-[11px] font-black text-slate-300 font-mono">
                            {progress > 2
                              ? (() => { const left = (elapsedClock / progress) * (100 - progress); return `${Math.floor(left / 60).toString().padStart(2, '0')}:${Math.floor(left % 60).toString().padStart(2, '0')}`; })()
                              : '--:--'}
                          </div>
                        </div>
                        <div>
                          <div className="text-[7px] text-slate-500 uppercase tracking-widest font-bold">Anomalies</div>
                          <div className="text-[11px] font-black text-white font-mono">{sessionFindings.length}</div>
                        </div>
                        <div>
                          <div className="text-[7px] text-slate-500 uppercase tracking-widest font-bold">Overlap</div>
                          <div className="text-[11px] font-black text-white font-mono">{forwardOverlap}%</div>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
              <div className="flex-1 relative bg-slate-400 border border-slate-500 overflow-hidden shadow-xl flex flex-col">
                <div className="p-4 border-b border-slate-500 bg-slate-500/20 flex justify-between items-center">
                  <h4 className="text-[10px] font-black text-slate-700 uppercase tracking-widest italic leading-none">
                    {(isUAV && combinedTrajectory && combinedTrajectory.length > 0) || (modelPoints && modelPoints.length > 0) ? "Spatiotemporal Mission Map" : "Audit Evidence Ledger"}
                  </h4>
                  <div className="flex items-center gap-2">
                    <span className="text-[10px] font-mono text-[#0000a9] uppercase font-black leading-none">
                      {(isUAV && combinedTrajectory && combinedTrajectory.length > 0) || (modelPoints && modelPoints.length > 0) ? "Mission Sync Active" : `${sessionFindings.length} Anomalies Logged`}
                    </span>
                  </div>
                </div>

                {/* ─── DEDUP MERGE BANNER ─── shown after run completes */}
                {isFinished && dedupStats && dedupStats.duplicatesRemoved > 0 && (
                  <div className="border-b border-slate-500 bg-[#0000a9]/10">
                    <div className="px-4 py-3 flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className="w-2 h-2 bg-[#1AFF80]"></div>
                        <div>
                          <div className="text-[11px] font-black text-[#0000a9] uppercase tracking-widest leading-none">
                            Dedup Complete · {dedupStats.duplicatesRemoved} Duplicates Merged
                          </div>
                          <div className="text-[9px] font-mono text-slate-700 uppercase tracking-widest mt-1">
                            {dedupStats.total} sightings → {dedupStats.unique} unique defects @ {dedupThresholdCm}cm
                          </div>
                        </div>
                      </div>
                      <button
                        onClick={() => setShowMergeReview(s => !s)}
                        className="text-[9px] font-black text-[#0000a9] uppercase tracking-widest hover:text-slate-900 transition px-3 py-1 border border-[#0000a9]/30 hover:border-[#0000a9]"
                      >
                        {showMergeReview ? 'Hide Review' : `Review ${dedupMergeGroups.length} Groups`}
                      </button>
                    </div>

                    {/* Expandable merge-group review */}
                    {showMergeReview && dedupMergeGroups.length > 0 && (
                      <div className="border-t border-slate-500/50 max-h-[260px] overflow-y-auto custom-scrollbar bg-slate-200/50">
                        {dedupMergeGroups.map((group, gIdx) => {
                          const canonTs = (group.canonical as any).timestamp;
                          const dupCount = group.duplicates.length;
                          const a = group.canonical.wallAnchor;
                          return (
                            <div key={gIdx} className="px-4 py-2 border-b border-slate-400/30 last:border-b-0 flex items-center justify-between text-[10px]">
                              <div className="flex items-center gap-3 flex-1 min-w-0">
                                <span className="font-mono text-slate-600 w-8">#{gIdx + 1}</span>
                                <span className="font-black uppercase text-slate-900 tracking-wide truncate flex-1">{group.canonical.type}</span>
                                <span className="font-mono text-slate-600 whitespace-nowrap">
                                  {a ? `(${a.x.toFixed(2)}, ${a.y.toFixed(2)}, ${a.z.toFixed(2)}) m` : 'no anchor'}
                                </span>
                              </div>
                              <div className="flex items-center gap-3 ml-4">
                                <span className="font-mono text-slate-700 whitespace-nowrap">1 + {dupCount} dup</span>
                                <span className="font-mono text-slate-500 whitespace-nowrap">t={canonTs ? Number(canonTs).toFixed(1) : '—'}s</span>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                )}
                <div className="flex-1 relative bg-slate-300 overflow-hidden">
                  {(isUAV && combinedTrajectory && combinedTrajectory.length > 0) || (modelPoints && modelPoints.length > 0) ? (
                    <TrajectoryMapT
                      points={combinedTrajectory}
                      minimal={false}
                      currentTime={currentGlobalMapTime}
                      baseTimestamp={0}
                      range={activeGlobalRange}
                      targetOverlap={forwardOverlap}
                      forceShowCaptureLocations={true}
                      modelPoints={modelPoints}
                      onClose={() => { }}
                      showCapOnly={showCapOnly}
                      initialCloudOffset={initialCloudOffset}
                    />
                  ) : (
                    <div className="absolute inset-0 overflow-y-auto p-4 custom-scrollbar bg-slate-300">
                      {sessionFindings.length === 0 ? (
                        <div className="h-full flex flex-col items-center justify-center opacity-20 text-center p-8">
                          <svg className="w-12 h-12 mb-4 text-slate-800" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" /></svg>
                          <span className="text-[10px] uppercase font-black tracking-widest text-slate-700 italic">Scanning mission footage for structural anomalies...</span>
                        </div>
                      ) : (
                        <div className="grid grid-cols-2 gap-3 pb-8">
                          {[...sessionFindings].reverse().map((analysis, revIdx) => {
                            const fIdx = sessionFindings.length - 1 - revIdx;
                            const finding = analysis.findings?.[0];
                            const isVerified = verifiedFindingsIdx.has(fIdx);
                            const [ymin, xmin, ymax, xmax] = finding?.box || [0, 0, 1000, 1000];
                            const zoomScale = Math.min(6, 1000 / Math.max(1, xmax - xmin, ymax - ymin));
                            const color = severityColors[finding?.severity || Severity.NONE];

                            return (
                              <div key={fIdx} className={`bg-slate-300 border overflow-hidden group transition-all animate-slideUp flex flex-col rounded-none relative ${isVerified ? 'border-[#0000a9] shadow-lg scale-[1.02]' : 'border-slate-500 hover:border-[#0000a9]/50 shadow-sm'}`}>
                                {isVerified && <div className="absolute left-0 top-0 bottom-0 w-1 bg-[#0000a9]"></div>}
                                <div className="aspect-video w-full bg-slate-900 relative overflow-hidden flex items-center justify-center">
                                  {analysis.frameImage ? (
                                    <>
                                      <img src={analysis.frameImage} className="absolute max-w-none origin-top-left" style={{ width: `${zoomScale * 100}%`, height: 'auto', left: `${50 - ((xmin + xmax) / 2000) * zoomScale * 100}%`, top: `${50 - ((ymin + ymax) / 2000) * zoomScale * 100}%` }} alt="Defect" />
                                      {/* Overlay box: positioned at bbox center in zoomed-image space, sized proportional to bbox */}
                                      <div className="absolute pointer-events-none" style={{
                                        left: `${50 - ((xmax - xmin) / 2000) * zoomScale * 100}%`,
                                        top: `${50 - ((ymax - ymin) / 2000) * zoomScale * 100}%`,
                                        width: `${((xmax - xmin) / 1000) * zoomScale * 100}%`,
                                        height: `${((ymax - ymin) / 1000) * zoomScale * 100}%`,
                                      }}>
                                        <div className="absolute inset-0 border-2 border-red-500/70 shadow-[0_0_20px_rgba(239,68,68,0.4)] rounded-none" />
                                        <div className="absolute top-0 left-0 w-3 h-3 border-t-2 border-l-2 border-red-500 -translate-x-px -translate-y-px" />
                                        <div className="absolute top-0 right-0 w-3 h-3 border-t-2 border-r-2 border-red-500 translate-x-px -translate-y-px" />
                                        <div className="absolute bottom-0 left-0 w-3 h-3 border-b-2 border-l-2 border-red-500 -translate-x-px translate-y-px" />
                                        <div className="absolute bottom-0 right-0 w-3 h-3 border-b-2 border-r-2 border-red-500 translate-x-px translate-y-px" />
                                      </div>
                                    </>
                                  ) : (
                                    <div className="text-slate-800 text-[8px] font-black uppercase">No Data</div>
                                  )}
                                  <div className="absolute top-2 left-2 px-2 py-1 bg-slate-300/90 backdrop-blur text-[8px] font-black font-mono text-slate-900 border border-slate-500">T+{formatTime(analysis.timestamp || 0)}s</div>
                                  <div className="absolute top-2 right-2 w-2 h-2 rounded-none shadow-[0_0_8px_currentColor]" style={{ backgroundColor: color, color }}></div>
                                </div>
                                <div className="p-3 flex flex-col gap-3">
                                  <h5 className="text-[10px] font-black text-slate-900 uppercase italic truncate pr-1">{finding?.type || 'Structural Anomaly'}</h5>
                                  <div className="flex gap-2">
                                    <button onClick={() => verifyFinding(fIdx)} className={`flex-1 py-1.5 text-[8px] font-black uppercase tracking-widest transition-all ${isVerified ? 'bg-[#0000a9] text-white shadow-md' : 'bg-slate-300 text-slate-800 border border-slate-400 hover:border-[#0000a9]'}`}>
                                      {isVerified ? 'VERIFIED' : 'SAVE'}
                                    </button>
                                    <button onClick={() => removeFinding(fIdx)} className="px-2 py-1.5 bg-slate-300 text-slate-800 hover:text-red-500 border border-slate-400 transition-all shadow-sm">
                                      <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" /></svg>
                                    </button>
                                  </div>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </div>

            {isProcessing ? (
              <button
                onClick={stopNeuralEngine}
                disabled={isAborting}
                className={`w-full py-5 mt-4 rounded-none font-black uppercase text-[14px] tracking-[0.4em] shadow-2xl active:scale-[0.99] transition-all border-b-4 shrink-0 ${isAborting ? 'bg-red-900 text-white/50 cursor-not-allowed border-red-950' : 'bg-red-600 hover:bg-red-500 text-white animate-pulse border-red-700'}`}
              >
                {isAborting ? 'HALTING NEURAL ENGINE...' : 'STOP NEURAL ENGINE'}
              </button>
            ) : (
              <div className="grid grid-cols-3 gap-4 mt-4 shrink-0">
                <button
                  onClick={handleContinue}
                  disabled={sessionFindings.length === 0}
                  className={`w-full py-5 rounded-none font-black uppercase text-[12px] tracking-[0.2em] shadow-sm transition-all border-b-4 ${sessionFindings.length === 0 ? 'bg-slate-300 text-slate-500 border-slate-400 cursor-not-allowed' : 'bg-[#0000a9]/10 hover:bg-[#0000a9] text-[#0000a9] hover:text-white border-[#0000a9] hover:shadow-xl'}`}
                >
                  VIEW AUDIT DETAILS
                </button>
                <button
                  onClick={() => runDeepAnalysis(true)}
                  className="w-full py-5 rounded-none font-black uppercase text-[12px] tracking-[0.2em] shadow-sm hover:shadow-xl transition-all border-b-4 bg-[#0000a9] hover:bg-[#0000c0] text-white border-blue-900"
                >
                  ADD INSPECTION AUDIT
                </button>
                <button
                  onClick={() => runDeepAnalysis(false)}
                  disabled={sessionFindings.length === 0}
                  className={`w-full py-5 rounded-none font-black uppercase text-[12px] tracking-[0.2em] shadow-sm transition-all border-b-4 ${sessionFindings.length === 0 ? 'bg-slate-300 text-slate-500 border-slate-400 cursor-not-allowed' : 'bg-red-600 hover:bg-red-500 text-white border-red-700 hover:shadow-xl'}`}
                >
                  RE-AUDIT <span className="opacity-70 lowercase font-normal ml-2 tracking-normal">(clear old)</span>
                </button>
              </div>
            )}

            <div className="h-20 mt-4 bg-slate-400 shadow-xl rounded-none border border-slate-500 flex items-center px-8 gap-8 relative z-10 shrink-0">
              <div className="flex items-center gap-3">
                <button onClick={() => togglePlayback()} disabled={isProcessing} className={`w-12 h-12 flex items-center justify-center transition-all shadow-md ${isPlaying ? 'bg-[#0000a9] text-white' : 'bg-slate-300 text-slate-900 border border-slate-400 hover:bg-[#0000a9] hover:text-white'}`} > {isPlaying ? <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24"><path d="M6 19h4V5H6v14zm8-14v14h4V5h-4z" /></svg> : <svg className="w-6 h-6 ml-1" fill="currentColor" viewBox="0 0 24 24"><path d="M8 5v14l11-7z" /></svg>} </button>
                <button onClick={stopPlayback} disabled={isProcessing} className="w-12 h-12 bg-slate-300 text-slate-800 flex items-center justify-center hover:bg-red-500 hover:text-white transition-all border border-slate-400 shadow-sm" > <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24"><path d="M6 6h12v12H6z" /></svg> </button>
              </div>
              <div className="flex items-center gap-1 bg-slate-300 p-1.5 border border-slate-400 shadow-inner">
                <button onClick={() => stepFrame(-150)} className="px-4 py-2 hover:bg-slate-400 text-slate-700 hover:text-slate-900 transition"> <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M11 19l-7-7 7-7m8 14l-7-7 7-7" /></svg> </button>
                <button onClick={() => stepFrame(-1)} className="px-4 py-2 hover:bg-slate-400 text-slate-700 hover:text-slate-900 transition font-mono text-[11px] font-black uppercase tracking-tighter"> -1F </button>
                <div className="w-px h-6 bg-slate-400 mx-2"></div>
                <button onClick={() => stepFrame(1)} className="px-4 py-2 hover:bg-slate-400 text-slate-700 hover:text-slate-900 transition font-mono text-[11px] font-black uppercase tracking-tighter"> +1F </button>
                <button onClick={() => stepFrame(150)} className="px-4 py-2 hover:bg-slate-400 text-slate-700 hover:text-slate-900 transition"> <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 5l7 7-7 7M5 5l7 7-7 7" /></svg> </button>
              </div>
              <div className="flex flex-col ml-auto text-right">
                <span className="text-[10px] text-slate-800 font-black uppercase tracking-widest leading-none mb-1">Telemetry Sync Time</span>
                <span className="text-xl font-mono font-black text-slate-900 leading-none"> T+{formatTime(currentGlobalMapTime)}s </span>
              </div>
            </div>
          </>
        ) : (
          <div className="w-full h-full flex flex-col items-center justify-center gap-12 bg-slate-300">
            <div className="w-full max-w-7xl aspect-video flex flex-col items-center justify-center gap-12 bg-slate-400 border border-slate-500 shadow-[0_40px_100px_rgba(0,0,169,0.1)] relative rounded-none">
              <h3 className="text-3xl font-black uppercase tracking-tighter text-[#0000a9] italic">Load Telemetry Data <span className="text-slate-700 font-light not-italic">| T Registry</span></h3>
              <div className="w-full max-w-6xl z-10 px-12 grid grid-cols-3 gap-12">
                <div className="flex flex-col gap-6 bg-slate-300 p-8 border border-slate-400 shadow-sm">
                  <h4 className="text-[10px] font-black text-slate-700 uppercase tracking-widest mb-2 leading-none">Load Files</h4>
                  <div className="grid grid-cols-1 gap-4">
                    <button onClick={() => stageVideoRef.current?.click()} className={`group relative h-28 flex items-center gap-6 p-6 text-left transition-all border shadow-sm ${pendingVideo ? 'bg-slate-400 border-[#0000a9] shadow-[#0000a9]/10' : 'bg-slate-400 border-slate-500 hover:border-[#0000a9]'}`} > <div className={`w-16 h-16 shrink-0 flex items-center justify-center transition-all ${pendingVideo ? 'bg-[#0000a9] text-white shadow-lg' : 'bg-slate-300 text-slate-800'}`}> <svg className="w-10 h-10" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2-2v12a2 2 0 002 2z" /></svg> </div> <div className="z-10 overflow-hidden"> <h3 className="font-black text-xs uppercase text-slate-900 tracking-wide mb-1">Video File</h3> <p className="text-[10px] text-slate-800 font-mono truncate uppercase font-bold">{pendingVideo ? pendingVideo.name : 'MP4 / MOV VIDEO SOURCE'}</p> </div> <input type="file" ref={stageVideoRef} className="hidden" accept="video/*" onChange={handleStageVideo} /> </button>
                    <button onClick={() => stageCsvRef.current?.click()} className={`group relative h-28 flex items-center gap-6 p-6 text-left transition-all border shadow-sm ${pendingTrajectory || (trajectoryPoints && trajectoryPoints.length > 0) ? 'bg-slate-400 border-[#0000a9] shadow-[#0000a9]/10' : 'bg-slate-400 border-slate-500 hover:border-[#0000a9]'}`} > <div className={`w-16 h-16 shrink-0 flex items-center justify-center transition-all ${pendingTrajectory || (trajectoryPoints && trajectoryPoints.length > 0) ? 'bg-[#0000a9] text-white shadow-lg' : 'bg-slate-300 text-slate-800'}`}> <svg className="w-10 h-10" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M9 20l-5.447-2.724A1 1 0 013 16.382V5.618a1 1 0 011.447-.894L9 7m0 13l6-3m-6 3V7m6 10l4.553 2.276A1 1 0 0021 18.382V7.618a1 1 0 00-1.447-.894L15 7m0 10V7m0 0L9 4" /></svg> </div> <div className="z-10 overflow-hidden"> <h3 className="font-black text-xs uppercase text-slate-900 tracking-wide mb-1">Telemetry CSV</h3> <p className="text-[10px] text-slate-800 font-mono uppercase font-bold">{pendingTrajectory ? 'CSV DATA VALIDATED' : (trajectoryPoints && trajectoryPoints.length > 0 ? 'SOURCE LINKED' : 'UAV FLIGHT LOG .CSV')}</p> </div> <input type="file" ref={stageCsvRef} className="hidden" accept=".csv" onChange={handleStageCsv} /> </button>
                  </div>
                  <button onClick={addFlightSegment} disabled={!pendingVideo || (isUAV && !pendingTrajectory && !(trajectoryPoints && trajectoryPoints.length > 0))} className={`w-full py-5 mt-2 font-black uppercase text-[11px] tracking-widest transition-all flex items-center justify-center gap-3 shadow-lg ${pendingVideo && (pendingTrajectory || (trajectoryPoints && trajectoryPoints.length > 0) || !isUAV) ? 'bg-[#0000a9] text-white hover:bg-[#0000c0]' : 'bg-slate-400 text-slate-800 opacity-80'}`} > <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M12 6v6m0 0v6m0-6h6m-6 0H6" /></svg> + ADD </button>
                </div>
                <div className="flex flex-col gap-6 bg-slate-300 p-8 border border-slate-400 shadow-sm overflow-hidden">
                  <h4 className="text-[10px] font-black text-slate-700 uppercase tracking-widest mb-2 leading-none">Asset Selection</h4>
                  <div className="flex-1 overflow-y-auto max-h-[300px] custom-scrollbar space-y-4 pr-3">
                    {segments.length === 0 ? <div className="h-full min-h-[160px] flex items-center justify-center text-[11px] text-slate-800 font-black uppercase border border-dashed border-slate-500 bg-slate-400 shadow-inner">MANIFEST_NULL</div> : segments.map((seg, i) => <div key={seg.id} onClick={() => setActiveSegmentIndex(i)} className={`p-4 border transition-all flex gap-5 cursor-pointer shadow-sm relative overflow-hidden ${activeSegmentIndex === i ? 'bg-slate-400 border-[#0000a9] shadow-[#0000a9]/5' : 'bg-slate-400 border-slate-500 hover:border-[#0000a9]/30'}`} > {activeSegmentIndex === i && <div className="absolute left-0 top-0 bottom-0 w-1 bg-[#0000a9]"></div>} <div className="w-24 h-14 bg-slate-900 border border-slate-500 overflow-hidden shrink-0 shadow-inner"><video src={seg.videoUrl} className="w-full h-full object-cover opacity-80" muted /></div> <div className="flex-1 flex justify-between items-center overflow-hidden"><div className={`text-[10px] font-black uppercase truncate pr-4 leading-none italic ${activeSegmentIndex === i ? 'text-[#0000a9]' : 'text-slate-700'}`}>SEG_{i + 1} | {seg.video.name}</div><button onClick={(e) => { e.stopPropagation(); removeSegment(seg.id); }} className="p-2 text-slate-800 hover:text-red-500 transition-colors bg-slate-300 rounded-none border border-slate-400"><svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M6 18L18 6M6 6l12 12" /></svg></button></div></div>)}
                  </div>
                </div>
                <div className="flex flex-col gap-6 bg-slate-300 p-8 border border-slate-400 shadow-sm">
                  <h4 className="text-[10px] font-black text-slate-700 uppercase tracking-widest mb-2 leading-none">Load 3D Model</h4>
                  <div className="flex-1">
                    <button onClick={() => lasInputRef.current?.click()} className={`group relative w-full h-full flex flex-col justify-center items-center gap-8 p-10 text-center transition-all border shadow-sm ${lasModelName ? 'bg-slate-400 border-[#0000a9] shadow-[#0000a9]/5' : 'bg-slate-400 border-slate-500 hover:border-[#0000a9]'}`} >
                      <div className={`w-20 h-20 flex items-center justify-center transition-all ${lasModelName ? 'bg-[#0000a9] text-white shadow-lg' : 'bg-slate-300 text-slate-800'}`}>
                        <svg className="w-10 h-10" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 00-2-2v-6a2 2 0 01-2-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" /></svg>
                      </div>
                      <div className="z-10 overflow-hidden">
                        <h3 className="font-black text-xs uppercase text-slate-900 tracking-wide mb-1">{lasModelName ? 'Source Linked' : 'IMPORT POINT CLOUD'}</h3>
                        <p className="text-[10px] text-slate-800 font-mono truncate px-4 font-bold uppercase">{lasModelName || '.LAS / .LAZ / .XYZ / .TXT'}</p>
                      </div>
                      <input type="file" ref={lasInputRef} className="hidden" accept=".las,.laz,.xyz,.txt,.csv" onChange={handleLasUpload} />
                    </button>
                  </div>
                </div>
              </div>
              <button disabled={!canProceed} onClick={() => setComplianceAccepted(true)} className={`w-full max-w-7xl py-10 font-black uppercase text-[15px] tracking-[0.6em] transition-all italic border-b-4 ${canProceed ? 'bg-[#0000a9] text-white shadow-[0_30px_60px_rgba(0,0,169,0.2)] border-blue-900 hover:translate-y-[-2px]' : 'bg-slate-300 text-slate-800 border-slate-400 cursor-not-allowed'}`} > AI DEFECT DETECTION </button>
            </div>
          </div>
        )}
      </div>

      {videoUrl && complianceAccepted && (
        <div className="p-8 bg-slate-400 border-t border-slate-500 space-y-10 overflow-y-auto max-h-[35vh] custom-scrollbar text-left">
          <div className="transition-all duration-300">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
              <div className="space-y-6">
                <h4 className="text-[10px] font-black text-slate-700 uppercase tracking-widest border-b border-slate-500 pb-3 italic leading-none">Global Mission Protocol</h4>
                <div className="grid grid-cols-2 gap-4 w-full">
                  {PROTOCOL_OPTIONS.map(protocol => {
                    const isActive = inspectionModes.includes(protocol.id);
                    return (
                      <button
                        key={protocol.id}
                        onClick={() => toggleMode(protocol.id)}
                        className={`relative group p-5 border text-left transition-all overflow-hidden rounded-none shadow-sm ${isActive ? 'bg-[#0000a9] border-[#0000a9] shadow-[0_0_15px_rgba(0,0,169,0.3)]' : 'bg-slate-300 border-slate-400 hover:border-[#0000a9]/40 hover:bg-slate-400'}`}
                      >
                        <div className="flex flex-col h-full justify-between gap-4 relative z-10">
                          <div className="flex justify-between items-start">
                            <div className={`w-8 h-8 transition-colors duration-300 ${isActive ? 'text-white' : 'text-slate-800 group-hover:text-slate-700'}`}>
                              {protocol.icon}
                            </div>
                            {isActive && <div className="w-2 h-2 bg-slate-300 animate-pulse shadow-[0_0_8px_white]"></div>}
                          </div>
                          <div>
                            <h3 className={`font-black text-[10px] uppercase tracking-widest leading-none mb-1 ${isActive ? 'text-white' : 'text-slate-900 group-hover:text-[#0000a9]'}`}>
                              {protocol.label}
                            </h3>
                            <p className={`text-[9px] font-bold leading-tight uppercase tracking-tight ${isActive ? 'text-white/80' : 'text-slate-800'}`}>
                              {protocol.desc}
                            </p>
                          </div>
                        </div>
                      </button>
                    );
                  })}
                </div>

                <div className="pt-2">
                  <div className="flex items-center justify-between p-6 bg-slate-300 border border-slate-400 shadow-sm rounded-none">
                    <div className="flex flex-col gap-1">
                      <span className="text-[10px] font-black text-[#0000a9] uppercase tracking-widest leading-none">Temporal Windowing Filter</span>
                      <span className="text-[9px] text-slate-800 font-bold uppercase tracking-tight">Show frustums only within range</span>
                    </div>
                    <button
                      onClick={() => setShowCapOnly(!showCapOnly)}
                      className={`w-14 h-7 rounded-none transition-all relative p-1 shadow-inner ${showCapOnly ? 'bg-[#0000a9]' : 'bg-slate-400 border border-slate-500'}`}
                    >
                      <div className={`w-5 h-5 transition-all shadow-md ${showCapOnly ? 'bg-slate-300 translate-x-7' : 'bg-slate-400 translate-x-0'}`}></div>
                    </button>
                  </div>
                </div>
              </div>
              <div className="space-y-8">
                <h4 className="text-[10px] font-black text-slate-700 uppercase tracking-widest border-b border-slate-500 pb-3 italic text-left leading-none">Mission Spatiotemporal Range</h4>
                <div className="space-y-8 pt-2">
                  <div className="space-y-4">
                    <div className="flex justify-between text-[11px] font-black uppercase tracking-widest"> <span className="text-slate-700">Mission Inception</span> <span className="text-[#0000a9]">{formatTime(rangeStart)}s</span> </div>
                    <input type="range" min="0" max={videoDuration} step="0.1" value={rangeStart} onChange={(e) => handleRangeChange(e, true)} disabled={isProcessing} className={`w-full h-1 bg-white rounded-none appearance-none accent-[#0000a9] ${isProcessing ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`} />
                  </div>
                  <div className="space-y-4">
                    <div className="flex justify-between text-[11px] font-black uppercase tracking-widest"> <span className="text-slate-700">Mission Termination</span> <span className="text-red-500">{formatTime(rangeEnd)}s</span> </div>
                    <input type="range" min="0" max={videoDuration} step="0.1" value={rangeEnd} onChange={(e) => handleRangeChange(e, false)} disabled={isProcessing} className={`w-full h-1 bg-white rounded-none appearance-none accent-red-500 ${isProcessing ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`} />
                  </div>
                </div>
              </div>
              <div className="space-y-8">
                <div className="space-y-8">
                  <h4 className="text-[10px] font-black text-slate-700 uppercase tracking-widest border-b border-slate-500 pb-3 italic text-left leading-none">Technical Parameters</h4>
                  <div className="space-y-4 pt-1"> <div className="flex justify-between text-[11px] font-black uppercase tracking-widest"><span className="text-slate-700">Audit Sensitivity</span><span className="text-[#0000a9]">LVL {sensitivity}</span></div> <input type="range" min="1" max="10" step="1" value={sensitivity} onChange={(e) => setSensitivity(parseInt(e.target.value))} disabled={isProcessing} className={`w-full h-1 bg-white rounded-none appearance-none accent-[#0000a9] ${isProcessing ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`} /> </div>
                  <div className="space-y-4 pt-1"> <div className="flex justify-between text-[11px] font-black uppercase tracking-widest"> <span className="text-slate-700">Reconstruction Overlap</span> <span className="text-[#0000a9]">{forwardOverlap}%</span> </div> <input type="range" min="0" max="50" step="1" value={forwardOverlap} onChange={(e) => setForwardOverlap(parseInt(e.target.value))} disabled={isProcessing} className={`w-full h-1 bg-white rounded-none appearance-none accent-[#0000a9] ${isProcessing ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`} /> </div>
                  <div className="space-y-4 pt-1"> <div className="flex justify-between text-[11px] font-black uppercase tracking-widest"> <span className="text-slate-700">Dedup Threshold</span> <span className="text-[#0000a9]">{dedupThresholdCm} cm</span> </div> <input type="range" min="2" max="30" step="1" value={dedupThresholdCm} onChange={(e) => setDedupThresholdCm(parseInt(e.target.value))} disabled={isProcessing} className={`w-full h-1 bg-white rounded-none appearance-none accent-[#0000a9] ${isProcessing ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`} /> </div>
                  <div className="space-y-4 pt-1"> <div className="flex justify-between text-[11px] font-black uppercase tracking-widest"> <span className="text-slate-700">Wiggle Displacement</span> <span className="text-[#0000a9]">{wiggleDisplacementCm} cm</span> </div> <input type="range" min="5" max="25" step="1" value={wiggleDisplacementCm} onChange={(e) => setWiggleDisplacementCm(parseInt(e.target.value))} disabled={isProcessing} className={`w-full h-1 bg-white rounded-none appearance-none accent-[#0000a9] ${isProcessing ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`} /> </div>
                  <div className="space-y-3 pt-1">
                    <div className="text-[11px] font-black uppercase tracking-widest text-slate-700">Neural Resolution</div>
                    <div className="flex flex-col gap-2">
                      {IMAGE_RESOLUTION_OPTIONS.map(opt => (
                        <button
                          key={opt.mode}
                          type="button"
                          onClick={() => !isProcessing && setResolutionMode(opt.mode)}
                          disabled={isProcessing}
                          className={`w-full text-left px-4 py-2.5 border transition-all flex justify-between items-center rounded-none shadow-sm ${resolutionMode === opt.mode
                            ? 'bg-[#0000a9] border-[#0000a9] text-white'
                            : 'bg-slate-300 border-slate-400 text-slate-800 hover:border-[#0000a9]/60'
                            } ${isProcessing ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer hover:shadow-md'}`}
                        >
                          <span className="text-[10px] font-black uppercase tracking-widest">{opt.label}</span>
                          <span className={`text-[9px] font-bold uppercase tracking-tight ${resolutionMode === opt.mode ? 'text-white/70' : 'text-slate-600'}`}>{opt.desc.split('·').pop()?.trim()}</span>
                        </button>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          {/* Neural Engine controls moved higher */}
        </div>
      )}
      <canvas ref={canvasRef} className="hidden" />
      {isArchiving && <LoadingOverlay message="ARCHIVING MISSION ASSETS" subMessage="COMMITTING VIDEO LOGS TO LOCAL PROJECT DIRECTORY" />}
    </div>
  );
};
